import React from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';

import { BodyText, DisplayText } from '../../Text';
import { theme } from '@garnish/constants';
import { useResponsive } from '../../../hooks';
import { HorizontalScrollRailCount } from '../../HorizontalScrollRail/subcomponents/HorizontalScrollRailCount';

// ────────────────────────────────────────────────────────────────────────────────

export const SectionHeader: React.FC<SectionHeaderProps> = (props) => {
  const {
    heading,
    subtitle,
    count,
    children,
    variation = 'body',
    withoutBorder,
    style,
  } = props;
  const hasCounter = Boolean(count);

  const TitleWrapper = hasCounter ? Row : React.Fragment;

  return (
    <>
      <Wrapper withoutBorder={withoutBorder} style={style}>
        <TitleWrapper>
          <Title variation={variation}>{heading}</Title>
          <HorizontalScrollRailCount count={count ?? 0} />
        </TitleWrapper>
        <ChildrenWrapper>{children}</ChildrenWrapper>
      </Wrapper>
      <Subtitle subtitle={subtitle} />
    </>
  );
};

// ─── SUBCOMPONENTS ──────────────────────────────────────────────────────────────

const Row = ({ children }: Pick<ViewProps, 'children'>) => (
  <View style={styles.titleRuler}>{children}</View>
);

const Wrapper: React.FC<WrapperProps> = (props) => {
  const { withoutBorder, style, children } = props;
  const wrapperStyle = useWrapperStyle({ withoutBorder, style });

  return (
    <View style={wrapperStyle} accessibilityRole="header">
      {children}
    </View>
  );
};

const Subtitle: React.FC<SubtitleProps> = (props) => {
  const { subtitle } = props;

  if (!subtitle) return null;

  return (
    <BodyText style={styles.subtitle} size={3}>
      {subtitle}
    </BodyText>
  );
};

const Title: React.FC<TitleProps> = (props) => {
  const { children, variation } = props;
  const { minWidth } = useResponsive();

  const titleFontSize = minWidth.isSM ? 3 : 2;

  if (variation === 'display')
    return <DisplayText size={titleFontSize}>{children}</DisplayText>;

  return <BodyText size={titleFontSize}>{children}</BodyText>;
};

const ChildrenWrapper: React.FC = (props) => {
  if (!checkIfHasChildren(props.children)) return null;

  return <View style={styles.childrenWrapper}>{props.children}</View>;
};

// ─── STYLES ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  wrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  wrapperWithBorder: {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.GRAY,
    paddingBottom: 6,
  },

  titleRuler: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['4'],
  },
  childrenWrapper: {
    paddingLeft: theme.spacing['4'],
  },
  subtitle: {
    paddingTop: theme.spacing['4'],
  },
});

const useWrapperStyle = (props: WrapperProps) => {
  const { withoutBorder, style } = props;

  const optionalBorderStyle = withoutBorder ? {} : styles.wrapperWithBorder;

  return [styles.wrapper, optionalBorderStyle, style];
};

// ─── UTILS ──────────────────────────────────────────────────────────────────────

function checkIfHasChildren(children: React.ReactNode) {
  return React.Children.count(children) > 0;
}

// ────────────────────────────────────────────────────────────────────────────────

type SectionHeaderProps = {
  readonly heading: string;
  readonly subtitle?: string;
  readonly variation?: 'body' | 'display';
  readonly withoutBorder?: boolean;
  readonly count?: number;
  readonly style?: ViewProps['style'];
};

type WrapperProps = Pick<SectionHeaderProps, 'withoutBorder' | 'style'>;

type TitleProps = Pick<SectionHeaderProps, 'variation'>;

type SubtitleProps = Pick<SectionHeaderProps, 'subtitle'>;
